import type { ReactNode } from 'react';

interface LockedRevealProps {
  children: ReactNode;
  /** What the user gets if they unlock — short phrase, e.g. "all 10 careers". */
  unlockLabel: string;
  /** Where the unlock CTA scrolls/links to. Default: '#pricing'. */
  href?: string;
  /** Visual blur strength. Defaults to 'md' (4px-ish). */
  blur?: 'sm' | 'md' | 'lg';
  /** Extra classes for the outer wrapper. */
  className?: string;
}

const BLUR_CLASS = {
  sm: 'blur-[3px]',
  md: 'blur-[6px]',
  lg: 'blur-[10px]',
} as const;

/**
 * Wraps content with a blur + overlay + click-to-unlock CTA. Used across
 * /pricing to tease parts of the AI report without giving them away. The
 * whole element is clickable and routes to the pricing section.
 */
export function LockedReveal({
  children,
  unlockLabel,
  href = '#pricing',
  blur = 'md',
  className = '',
}: LockedRevealProps) {
  return (
    <a
      href={href}
      onClick={(e) => {
        if (href === '#pricing') {
          e.preventDefault();
          document
            .getElementById('pricing')
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }}
      className={`group relative block overflow-hidden rounded-2xl ${className}`}
      aria-label={`Unlock ${unlockLabel}`}
    >
      {/* Blurred content (purely visual; aria-hidden so screen readers
          announce the unlock CTA, not the locked text). */}
      <div className={`${BLUR_CLASS[blur]} select-none pointer-events-none`} aria-hidden>
        {children}
      </div>

      {/* Click overlay — slight gradient + lock badge. */}
      <div className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-white via-white/85 to-white/0 transition-opacity">
        <div className="w-full p-5 sm:p-6 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-ink text-white text-xs font-bold uppercase tracking-wider px-4 py-2 shadow-cardLg group-hover:bg-brand group-hover:text-ink transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Unlock {unlockLabel}
          </span>
        </div>
      </div>
    </a>
  );
}
